'use client';

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { NextStep, NextStepProvider } from 'nextstepjs';
import { useTheme } from "@/providers/ThemeProvider";
import DynamicTourCard from './dynamic-tour-card';
import { getTourSteps, tourStepsData } from './ui-tour-steps';
import { getOverlayColor } from './theme-utils';
import { useTranslations } from 'next-intl';

// Pre-defined transition object to avoid recreation
const CUSTOM_TRANSITION = {
  type: "tween",
  duration: 0.25,
  ease: "easeOut"
};

// Interface for component props
interface TourProviderProps {
  children: React.ReactNode;
  onComplete?: (tourName?: string | null) => void;
  onSkip?: (step: number, tourName?: string | null) => void;
  onStepChange?: (step: number, tourName?: string | null) => void;
}

/**
 * Main Tour Provider component with translations and responsive design
 */
export const TourProvider: React.FC<TourProviderProps> = ({
  children,
  onComplete,
  onSkip,
  onStepChange
}) => {
  const { isDarkMode } = useTheme();
  const [isMobile, setIsMobile] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Memoize shadow color calculation
  const shadowColor = useMemo(() => (
    { rgb: "0, 0, 0", opacity: isDarkMode ? "0.75" : "0.6" }
  ), [isDarkMode]);

  // Get translations
  const t = useTranslations("Tour");

  // Apply translations to the tour steps - memoized for performance
  const getTranslatedSteps = useCallback(() => {
    const translatedSteps = tourStepsData.map(tour => {
      const translatedTourSteps = tour.steps.map(step => {
        // Create translation keys based on step title if it's a string
        let titleKey = '';
        if (typeof step.title === 'string') {
          titleKey = step.title.toLowerCase().replace(/\s+/g, '_').replace(/[^\w_]/g, '');
        }

        const translatedStep = {
          ...step,
        };

        // Only translate if it's a string, not a JSX element
        if (typeof step.title === 'string') {
          translatedStep.title = t(`${titleKey}.title`, step.title);
        }

        if (typeof step.content === 'string') {
          translatedStep.content = t(`${titleKey}.content`, step.content);
        }

        // Translate coach image alt text if present
        if (step.coachImage && typeof step.coachImage.alt === 'string') {
          translatedStep.coachImage = {
            ...step.coachImage,
            alt: t('meet_finn.alt', step.coachImage.alt)
          };
        }

        return translatedStep;
      });

      return {
        ...tour,
        steps: translatedTourSteps
      };
    });

    return translatedSteps;
  }, [t]);

  // Detect mobile screen size - with debounce
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Check on initial render
    checkMobile();

    // Debounce resize handler for better performance
    let resizeTimer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(checkMobile, 100);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Memoize filtered/translated steps
  const baseSteps = useMemo(() => getTourSteps(isMobile), [isMobile]);
  const translatedSteps = useMemo(() => {
    const translated = getTranslatedSteps();
    return translated.map(tour => {
      // Find the filtered steps for this tour
      const filteredTour = baseSteps.find(filteredTour => filteredTour.tour === tour.tour);

      if (!filteredTour) return tour;

      // Get the list of step indices that should be shown based on filtering
      const filteredStepIndices = new Set();
      filteredTour.steps.forEach(step => {
        const index = tour.steps.findIndex(s =>
          s.title === step.title &&
          s.selector === step.selector
        );
        if (index !== -1) {
          filteredStepIndices.add(index);
        }
      });

      // Keep only the steps that should be shown based on device filtering
      const finalSteps = tour.steps.filter((_, index) => filteredStepIndices.has(index));

      return {
        ...tour,
        steps: finalSteps
      };
    });
  }, [baseSteps, getTranslatedSteps]);

  // Update overlay color when step changes
  const handleStepChange = useCallback((step: number, tourName?: string | null) => {
    setCurrentStepIndex(step);

    // Pass the step change to parent if needed
    if (onStepChange) {
      onStepChange(step, tourName);
    }
  }, [onStepChange]);

  // Memoize dynamic shadow color based on step and theme
  const dynamicShadowColor = useMemo(() =>
    getOverlayColor(currentStepIndex, isDarkMode),
    [currentStepIndex, isDarkMode]);

  return (
    <NextStep
      steps={translatedSteps}
      cardComponent={DynamicTourCard}
      cardTransition={CUSTOM_TRANSITION}
      shadowRgb={dynamicShadowColor.rgb}
      shadowOpacity={dynamicShadowColor.opacity}
      clickThroughOverlay={false}
      displayArrow={true}
      onComplete={onComplete}
      onSkip={onSkip}
      onStepChange={handleStepChange}
      pointerPadding={isMobile ? 8 : 12}
      pointerRadius={8}
    >
      {children}
    </NextStep>
  );
};

/**
 * Root NextStepProvider wrapper
 */
export const TourSystem: React.FC<{
  children: React.ReactNode
}> = ({
  children
}) => {
  return (
    <NextStepProvider>
      {children}
    </NextStepProvider>
  );
};

export default TourProvider;
